/**
 * Утилита для тестирования через query-параметры
 * // URL: ?widget-uuid=123&widget-title=Поддержка&require-phone=true&hide-trigger-button=true&widget-locale=en
 * const params = getWidgetQueryParams();
 * // { widgetUuid: '123', widgetTitle: 'Поддержка', requirePhone: true, hideTriggerButton: true, widgetLocale: 'en' }
 */
import { getTracerModules } from '@apptracer/sdk/lite';

import type { WidgetTheme } from '../../types/theme';
import { isWidgetTheme } from '../../types/theme';

import { packageName } from '../../../config';

interface WidgetQueryParams {
  widgetUuid?: string | null;
  widgetTitle?: string | null;
  companyLogoSrc?: string | null;
  widgetTheme?: WidgetTheme | null;
  panelOffsetTop?: number | null;
  entryPointGateway?: string | null;
  requirePhone?: boolean | null;
  hideTriggerButton?: boolean | null;
  widgetLocale?: string | null;
}

/**
 * Утилита для тестирования через query-параметры
 *
 * @example
 * // URL: ?widget-uuid=123&widget-title=Поддержка&require-phone=true&hide-trigger-button=true&widget-locale=en
 * const params = getWidgetQueryParams();
 * // { widgetUuid: '123', widgetTitle: 'Поддержка', requirePhone: true, hideTriggerButton: true, widgetLocale: 'en' }
 */

export const getWidgetQueryParams = (): WidgetQueryParams => {
  const tracerError = getTracerModules(packageName).error;

  try {
    const params = new URLSearchParams(window.location.search);

    const getString = (key: string): string | null => {
      const value = params.get(key);
      return value || null;
    };

    const getBoolean = (key: string): boolean | null => {
      const value = params.get(key);
      if (value === null) {
        return null;
      }
      return value.toLowerCase() === 'true';
    };

    const getNumber = (key: string): number | null => {
      const value = params.get(key);
      if (value === null) {
        return null;
      }
      const num = Number(value);
      return Number.isNaN(num) ? null : num;
    };

    const getTheme = (key: string): WidgetTheme | null => {
      const value = params.get(key);
      if (value === null) {
        return null;
      }
      return isWidgetTheme(value) ? value : null;
    };

    return {
      widgetUuid: getString('widget-uuid'),
      widgetTitle: getString('widget-title'),
      companyLogoSrc: getString('company-logo-src'),
      widgetTheme: getTheme('widget-theme'),
      panelOffsetTop: getNumber('panel-offset-top'),
      entryPointGateway: getString('entry-point-gateway'),
      requirePhone: getBoolean('require-phone'),
      hideTriggerButton: getBoolean('hide-trigger-button'),
      widgetLocale: getString('widget-locale'),
    };
  } catch (_) {
    tracerError?.error('Ошибка получения параметров виджета из URL', {
      keys: {
        url: window.location.href,
      },
    });
    return {};
  }
};
